import Model, { toModel, toModels, toProp } from "./Model";
import User from "./User";
import Observation from "./Observation";
import SDMEncounter from "./SDMEncounter";
import moment from 'moment'
import {UCUMUnit} from "../helper/UCUMUnit";

/**
 *
 * @class PatientSummary
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/model/PatientSummary.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

class PatientSummary extends Model {
  constructor(modelData) {
    super()

    /** @type User */
    this.user = toModel(User)

    /** @type Observation */
    this.bloodPressure = toModel(Observation)

    /** @type Observation */
    this.weight = toModel(Observation)

    /** @type Observation */
    this.HbA1c = toModel(Observation)

    /** @type Observation */
    this.HbA1cInPreferredUnit = toModel(Observation)

    /** @type Observation */
    this.cholesterol = toModel(Observation)

    /** @type Observation */
    this.HDL = toModel(Observation)

    /** @type Observation */
    this.LDL = toModel(Observation)

    /** @type Observation */
    this.LDLInPreferredUnit = toModel(Observation)

    /** @type SDMEncounter[] */
    this.SDMEncounters = toModels(SDMEncounter)

    this.patientSettings = toProp()

    this.behaviourComponents = toProp()

    this.behaviourGoals = toProp()

    this.outcomeComponents = toProp()

    this.outcomeGoals = toProp()

    this.buildModel(modelData)
  }

  /**
   * @return {null|SDMEncounter}
   */
  getLastCompletedSDMEncounter() {
    this.checkProps('SDMEncounters')

    if (this.SDMEncounters && this.SDMEncounters.length) {
      return this.SDMEncounters.reduce((last, cur) => {
        if (!last) return cur
        if (cur.status === 'done' && cur.encounterDate >= last.encounterDate) return cur

        return last
      })
    }

    return null
  }

  getBehaviourGoal(behaviourGoalKey){
    const behaviourGoals = this.behaviourGoals.filter(behaviourGoal => behaviourGoal.key === behaviourGoalKey)
    return behaviourGoals.length ? behaviourGoals[0] : null
  }

  /**
   * The first version the patient is eligible for
   * This will return the first eligible version regardless whether it's currently activated or not (to check the current activated version use store.activatedVersion)
   *
   * version 1 reserved for enrollment moment
   * version 2 = first SDM encounter (m0)
   * ...
   * version 6 = last SDM encounter (m36)
   * @return {number}
   */
  getCurrentVersion() {
    const lastEncounter = this.getLastCompletedSDMEncounter()
    const lastVersion = lastEncounter ? lastEncounter.version : 1
    return lastEncounter ? Math.min(7, lastVersion + 1) : 1
  }

  getPatientSetting(path)
  {
    const patientSettings = this.patientSettings.filter(setting => setting.path === path)
    return patientSettings.length ? patientSettings[0].value : null
  }

  getMorningStartTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:MORNING:STARTTIMEMORNING")
  }

  getMorningEndTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:MORNING:ENDTIMEMORNING")
  }

  getNoonStartTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:NOON:STARTTIMENOON")
  }

  getNoonEndTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:NOON:ENDTIMENOON")
  }

  getAfterNoonStartTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:AFTERNOON:STARTTIMEAFTERNOON")
  }

  getAfterNoonEndTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:AFTERNOON:ENDTIMEAFTERNOON")
  }

  getEveningStartTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:EVENING:STARTTIMEEVENING")
  }

  getEveningEndTime()
  {
    return this.getPatientSetting("GENERAL:QUARTILES:EVENING:ENDTIMEEVENING")
  }

  getQuartile(time)
  {
    if (time instanceof moment) time = time.format('HH:mm')

    if (time >= this.getMorningStartTime() && time < this.getMorningEndTime())
      return 'MORNING'
    if (time >= this.getNoonStartTime() && time < this.getNoonEndTime())
      return 'NOON'
    if (time >= this.getAfterNoonStartTime() && time < this.getAfterNoonEndTime())
      return 'AFTERNOON'

    return 'EVENING'
  }


  getBloodPressureLabel()
  {
    if (!this.bloodPressure) return "";

    this.checkProps(
      'bloodPressure.component.code.coding.code',
    )

    const bloodPressures = {}

    this.bloodPressure.component.forEach(subComponent => {
      if (subComponent.code.coding && subComponent.code.coding[0].code === '8480-6')
        bloodPressures.systolic = subComponent.getValue()
      else if (subComponent.code.coding && subComponent.code.coding[0].code === '8462-4')
        bloodPressures.diastolic = subComponent.getValue()
    })

    return `${bloodPressures.systolic}/${bloodPressures.diastolic} ${UCUMUnit.MILLIMETER_OF_MERCURY.unit}`
  }

  getWeightLabel()
  {
    return this.weight ? this.weight.getValue() + ' ' + UCUMUnit.KILOGRAM.unit : ''
  }

  getLDLLabel()
  {
    return this.LDL ? `LDL: ${this.LDL.getValue() + ' ' + UCUMUnit.MILLIGRAM_PER_DECILITER.unit}` : ''
  }

  getHbA1cLabel()
  {
    return this.HbA1c ? this.HbA1c.getValue() + UCUMUnit.PERCENT.unit : ''
  }

  getMedicationAdherenceAnswerLabel()
  {
    // I never take my medication as prescribed = low
    // I rarely take my medication a prescribed = low
    // I sometimes take my medication as prescribed = low
    // I often take my medication as prescribed = medium
    // I mostly take my medication as prescribed = medium
    // I always take my medication as prescribed = high

    if(this.answerMA) {
      switch(this.answerMA) {
        case 'NEVER':
        case 'RARELY':
        case 'SOMETIMES':
          return this.$app.translationManager.$ucfirst(this.$app.translationManager.$t(`medication.low`));
        case 'OFTEN':
        case 'MOSTLY':
          return this.$app.translationManager.$ucfirst(this.$app.translationManager.$t(`caregiverDashboardRiskProfile.medium`));
        case 'ALWAYS':
          return this.$app.translationManager.$ucfirst(this.$app.translationManager.$t(`medication.high`));
      }
    }

    return '';
  }
}

export default PatientSummary;
